import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { 
  Bell, 
  Calendar, 
  Video, 
  AlertCircle, 
  CheckCircle,
  XCircle,
  Clock,
  Mail
} from 'lucide-react';

interface NotificationItemProps {
  notification: {
    id: number;
    type: string;
    title: string;
    message: string;
    metadata: {
      bookingId?: number;
      videoId?: number;
      actionUrl?: string;
      priority?: 'low' | 'medium' | 'high';
    };
    readAt?: string | null;
    read_at?: string | null;
    createdAt?: string;
    created_at?: string;
  };
  onClick: (notification: any) => void;
  onMarkAsRead: (id: number) => void;
}

const notificationIcons: Record<string, any> = {
  booking_approved: CheckCircle,
  booking_rejected: XCircle,
  booking_rescheduled: Calendar,
  booking_reminder: Clock,
  video_uploaded: Video,
  video_published: Video,
  admin_new_booking: Bell,
  default: Bell,
};

const notificationColors: Record<string, string> = {
  booking_approved: 'text-green-600',
  booking_rejected: 'text-red-600',
  booking_rescheduled: 'text-orange-600',
  video_uploaded: 'text-blue-600',
  video_published: 'text-purple-600',
  admin_new_booking: 'text-amber-500',
  default: 'text-gray-600',
};

export function NotificationItem({ 
  notification, 
  onClick, 
  onMarkAsRead 
}: NotificationItemProps) {
  const Icon = notificationIcons[notification.type] || notificationIcons.default;
  const iconColor = notificationColors[notification.type] || notificationColors.default;
  const readAt = notification.readAt || notification.read_at;
  const isUnread = !readAt;

  const handleClick = () => {
    if (isUnread) {
      onMarkAsRead(notification.id);
    }
    if (notification.metadata?.actionUrl) {
      onClick(notification);
    }
  };

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-4 hover:bg-white/5 cursor-pointer transition-colors',
        isUnread && 'bg-white/10',
        notification.metadata?.actionUrl && 'hover:bg-white/10'
      )}
      onClick={handleClick}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <p className={cn(
              'text-sm',
              isUnread ? 'text-white font-medium' : 'text-gray-400',
              notification.metadata?.actionUrl && 'hover:underline'
            )}>
              {notification.message}
            </p>
          </div>
          
          {isUnread && (
            <div className="flex-shrink-0">
              <div className="h-2 w-2 bg-white rounded-full" />
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-gray-500">
            {notification.createdAt
              ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })
              : ''
            }
          </p>
          {notification.metadata?.actionUrl && (
            <span className="text-xs text-white/60">
              {notification.metadata.actionUrl.startsWith('http') ? 'Open link ↗' : 'Click to view →'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}